import { useTranslation } from 'react-i18next'
import { Skeleton } from '@/components/ui/skeleton'
import { ListPagination } from '@/components/shared/ListPagination'
import { cn } from '@/lib/utils'
import { formatCurrency, formatDateTime } from '@/lib/format'
import {
  CONTRACT_STATUS_LABELS,
  type ApiList,
  type ContractHistory,
  type ContractStatus,
} from '@/types'
import clockIcon from '@/assets/contracts/detail/history-clock.svg'
import checkIcon from '@/assets/contracts/detail/history-check.svg'
import fileIcon from '@/assets/contracts/detail/history-file.svg'
import wrenchIcon from '@/assets/contracts/detail/history-wrench.svg'

const cardShadow =
  'shadow-[0px_8px_16px_-4px_rgba(38,45,66,0.1),0px_1px_4px_-4px_rgba(38,45,66,0.05)]'

type Variant = 'success' | 'info' | 'warning'

const VARIANT: Record<
  Variant,
  { bg: string; icon: string; w: number; h: number }
> = {
  success: { bg: 'bg-[#d1fae5]', icon: checkIcon, w: 13.167, h: 9.833 },
  info: { bg: 'bg-[#e0f2fe]', icon: fileIcon, w: 12.667, h: 15.333 },
  warning: { bg: 'bg-[#fbf3d9]', icon: wrenchIcon, w: 14.27, h: 14.27 },
}

const EVENT_VARIANT: Record<string, Variant> = {
  signed: 'success',
  renewed: 'success',
  created_from_renewal: 'success',
  auto_billed: 'success',
  active: 'success',
  created: 'info',
  sent: 'info',
  sent_for_signature: 'info',
  terminated: 'warning',
  expired: 'warning',
  declined: 'warning',
  signature_invalidated: 'warning',
  deleted: 'warning',
  suspended: 'warning',
}

function eventVariant(event: string): Variant {
  return EVENT_VARIANT[event] ?? 'info'
}

function statusLabel(status: string): string {
  if (status in CONTRACT_STATUS_LABELS) {
    return CONTRACT_STATUS_LABELS[status as ContractStatus]
  }

  return status.replace(/_/g, ' ')
}

function snapshotNote(
  snapshot: Record<string, unknown> | null,
  t: (key: string, opts?: Record<string, unknown>) => string
): string | null {
  if (!snapshot) return null
  const parts: string[] = []
  const successor = snapshot.successor_number
  if (typeof successor === 'string' && successor) {
    parts.push(t('detail.history.notes.successor', { number: successor }))
  }
  const origin = snapshot.origin_number
  if (typeof origin === 'string' && origin) {
    parts.push(t('detail.history.notes.origin', { number: origin }))
  }
  const oldStatus = snapshot.old
  const newStatus = snapshot.new
  if (typeof oldStatus === 'string' && typeof newStatus === 'string') {
    parts.push(
      t('detail.history.notes.transition', {
        old: statusLabel(oldStatus),
        next: statusLabel(newStatus),
      })
    )
  }
  const amount = snapshot.amount
  if (typeof amount === 'number') {
    parts.push(
      t('detail.history.notes.amount', {
        amount: formatCurrency(amount, 'EUR', 2, 'suffix'),
      })
    )
  }
  return parts.length ? parts.join(' — ') : null
}

function HistoryRow({ item }: { item: ContractHistory }) {
  const { t } = useTranslation('contracts')
  const variant = eventVariant(item.event)
  const visual = VARIANT[variant]
  const title = t(`detail.history.events.${item.event}`, {
    defaultValue: item.event.replace(/_/g, ' '),
  })
  const by = item.causer_name
    ? t('detail.history.byUser', { name: item.causer_name })
    : null
  const note = snapshotNote(item.snapshot, t)
  const detail = [by, note].filter(Boolean).join(' - ')

  return (
    <div className='flex w-full items-start gap-3 overflow-hidden rounded-xl border border-[#e7e9ef] bg-white p-3'>
      <span
        className={cn(
          'flex size-8 shrink-0 items-center justify-center overflow-hidden rounded-lg p-2',
          visual.bg
        )}
      >
        <img
          src={visual.icon}
          alt=''
          width={visual.w}
          height={visual.h}
          className='max-w-none'
          style={{ width: visual.w, height: visual.h }}
        />
      </span>
      <div className='flex min-w-0 flex-1 flex-col gap-1'>
        <p className='text-sm font-medium leading-[1.4] text-[#171a20] normal-case'>
          {title}
        </p>
        <div className='flex flex-wrap items-center gap-2'>
          <p className='text-xs font-normal leading-4 text-[#3d434e] normal-case'>
            {formatDateTime(item.created_at)}
          </p>
          {detail ? (
            <>
              <span
                className='size-1 shrink-0 rounded-full bg-[#b0b5c3]'
                aria-hidden
              />
              <p className='min-w-0 text-sm font-normal leading-[1.4] text-[#3d434e] normal-case'>
                {detail}
              </p>
            </>
          ) : null}
        </div>
      </div>
    </div>
  )
}

// Figma 3480:85261 — Contract History tab card.
export function ContractDetailHistoryCard({
  data,
  isLoading,
  isError,
  page,
  pageSize,
  onPageChange,
  onPageSizeChange,
}: {
  data: ApiList<ContractHistory> | undefined
  isLoading: boolean
  isError: boolean
  page: number
  pageSize: number
  onPageChange: (p: number) => void
  onPageSizeChange: (s: number) => void
}) {
  const { t } = useTranslation('contracts')
  const rows = data?.data ?? []

  return (
    <section
      className={cn(
        'flex flex-col overflow-hidden rounded-2xl border border-[#e7e9ef] bg-white',
        cardShadow
      )}
    >
      <div className='flex h-16 items-center gap-2 px-6 pb-2 pt-6'>
        <div className='flex min-w-0 flex-1 items-center gap-3'>
          <span className='flex size-8 shrink-0 items-center justify-center overflow-hidden rounded-lg bg-[#f5f6fa] p-2'>
            <img
              src={clockIcon}
              alt=''
              width={15.333}
              height={15.333}
              className='h-[15.333px] w-[15.333px] max-w-none'
            />
          </span>
          <h2 className='font-heading truncate text-sm font-semibold leading-[1.2] text-[#171a20]'>
            {t('detail.tabs.history')}
          </h2>
        </div>
      </div>

      <div className='flex flex-col gap-2 px-6 pb-6 pt-4'>
        {isLoading &&
          Array.from({ length: 4 }).map((_, i) => (
            <Skeleton key={i} className='h-16 w-full rounded-xl' />
          ))}
        {isError && !isLoading && (
          <p className='py-6 text-center text-sm text-destructive'>
            {t('detail.history.loadError')}
          </p>
        )}
        {!isLoading && !isError && rows.length === 0 && (
          <p className='py-6 text-center text-sm text-[#3d434e]'>
            {t('detail.history.empty')}
          </p>
        )}
        {!isLoading &&
          !isError &&
          rows.map(item => <HistoryRow key={item.id} item={item} />)}
      </div>

      <ListPagination
        className='border-t border-[#e7e9ef] pl-4 pr-6'
        metadata={data?.metadata}
        page={page}
        pageSize={pageSize}
        onPageChange={onPageChange}
        onPageSizeChange={onPageSizeChange}
        showShowingRange={false}
        typographyVariant='activityPreview'
      />
    </section>
  )
}
